/**
 * What: The page shown when the record a page was opened for does not exist (a deleted or out-of-date link).
 * Transport / data-flow: Pure presentation. The page passes its own heading and body from product-language,
 *   the Tests route, and the setup ID (when it has one) so Get in touch opens the support page with that context.
 */
import type { PageErrorName } from '../page-state'
import { shopifyComponentCopy } from '@testfeed/product-language'
import { HouseEmptyState, type HouseEmptyStateAction } from './imagery/HouseEmptyState'
import { houseImagery } from './imagery/houseImagery'
import { buildSupportViewUrl } from '../resultsNavigation'

export function PageNotFound({
  heading,
  body,
  setupId,
  errorName = 'not-found',
  onBack,
  action,
  secondaryAction,
}: {
  heading: string
  body: string
  /** Attached to the support request so the team can find the record the merchant was looking for. */
  setupId?: string
  /** The `data-page-error` marker; defaults to the not-found case, other full-page errors reuse this page. */
  errorName?: PageErrorName
  onBack: () => void
  /** Recovery for this state; absent retains the existing support action. */
  action?: HouseEmptyStateAction
  /** Absent retains Back to Tests; null removes it when Back is already the primary action. */
  secondaryAction?: HouseEmptyStateAction | null
}) {
  const copy = shopifyComponentCopy.pageNotFound
  const primary = action ?? { content: copy.contactSupport, url: buildSupportViewUrl({ setupId }) }
  const secondary = secondaryAction === undefined ? { content: copy.backToTests, onAction: onBack } : secondaryAction
  return (
    <s-section data-page-error={errorName}>
      <HouseEmptyState
        heading={heading}
        image={houseImagery.capabilityPending}
        action={primary}
        secondaryAction={secondary ?? undefined}
      >
        <s-paragraph>{body}</s-paragraph>
      </HouseEmptyState>
    </s-section>
  )
}
